import { Box } from "@mui/material";
import { NavbarFilters } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { createProductSearchOptions } from "~/config/searchProductConfig";
import NavSearchInput from "~/features/navbar/NavSearchInput";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import { useKeyDownHandler, useBarcodeScanner } from "~/lib/barCodeScan";
import { searchProduct } from "~/lib/helpers";
import { parseProductTempZone, TempZone } from "~/lib/tempZone";
import { nonNullable } from "~/lib/typeHelpers";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";

import { ProductResult } from "./ProductResult";
import {
  setPage,
  setScanState,
  setSearchedVariant,
  setScannedBarcode,
  setTempZoneFilters
} from "./cycleCountsNew.slice";

export function CycleCountsSearch() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isMultiTempGrid, tempZones } = useGetMultiTempGridInfo();

  const clientId = useAppSelector(selectUsersClientId);
  const selectedTempZoneFilters = useAppSelector(
    (state) => state.cycleCountsNewSlice.tempZoneFilters
  );

  useKeyDownHandler();

  const [autocompleteOptions, updateAutocompleteOptions] = useState<
    ProductResult[]
  >([] as ProductResult[]);
  const [searchText, setSearchText] = useState<string | null>(null);

  const tempZoneMenuItems = tempZones
    .map(parseProductTempZone)
    .filter(nonNullable)
    .map((zone) => ({
      translation: t(zone.toLowerCase() as TempZone),
      filter: zone
    }));

  const autocompleteProductSearch = async (
    input: string | null
  ): Promise<void> => {
    if (input === null || input === "" || !clientId) {
      updateAutocompleteOptions([]);
    } else {
      // fetch variants to get variantId to index counting-task as inventory or variant data doesn't exist in the task data
      const hits = await searchProduct(input);

      const hitsAsAutocompleteRecords = createProductSearchOptions({ hits });

      updateAutocompleteOptions([...hitsAsAutocompleteRecords]);
    }
  };

  useBarcodeScanner<string>({
    findScanMatch: (buffer: string) => {
      dispatch(setScannedBarcode(buffer));
      return buffer;
    },
    processScanMatch: async (buffer: string | null) => {
      if (!buffer) {
        dispatch(setScanState("error"));
        return;
      }
      const hits = await searchProduct(buffer);
      const hitsAsAutocompleteRecords = createProductSearchOptions({ hits });
      if (hitsAsAutocompleteRecords.length === 0) {
        dispatch(setScanState("error"));
        return;
      }
      if (hitsAsAutocompleteRecords.length === 1) {
        dispatch(setScanState("success"));
        dispatch(setPage(1));
        dispatch(setSearchedVariant(hitsAsAutocompleteRecords[0]));
      }
    },
    deps: []
  });

  return (
    <Box
      id="autostorePutaway-navbar-searchComponent"
      display="flex"
      flexGrow="1"
      justifyContent="center"
    >
      <NavSearchInput<ProductResult>
        textInput={searchText || ""}
        setTextInput={(text) => setSearchText(text)}
        isAutocomplete
        searchPlaceholder={t("search cycle counts on inventory")}
        autocompleteSearchCb={async (input) => {
          await autocompleteProductSearch(input);
        }}
        autocompleteOptions={autocompleteOptions}
        autocompleteLabelConstructor={(option: ProductResult): string =>
          option.displayText
        }
        groupBy={(option) => t(option.type as "inventory" | "bin")}
        selectCb={(result: ProductResult) => {
          dispatch(setPage(1));
          dispatch(setSearchedVariant(result));
        }}
        clearTextOnSelect
      />
      {isMultiTempGrid && !!tempZoneMenuItems.length && (
        <NavbarFilters
          menuItemList={tempZoneMenuItems}
          menuItemsTitle={t("temperature")}
          selectedMenuItems={selectedTempZoneFilters}
          onFilterData={(filters) => {
            dispatch(
              setTempZoneFilters(
                filters.map(parseProductTempZone).filter(nonNullable)
              )
            );
            dispatch(setPage(1));
          }}
          hasActiveFilters={selectedTempZoneFilters.length > 0}
          onResetFilters={() => {
            dispatch(setTempZoneFilters([]));
            dispatch(setPage(1));
          }}
        />
      )}
    </Box>
  );
}
